import React, { useEffect, useState } from "react";
import { ShieldCheck, Check } from "lucide-react";

import JourneyMessageScreen from "@/b2b/components/JourneyMessageScreen";
import SupportContactActions from "@/b2b/components/SupportContactActions";
import config from "@/config";

/**
 * The screen a tenant sees when their journey has stopped and only ops can restart it.
 *
 * There are two such dead ends — the bank-check attempt limit, and a banking review that never
 * finished — and they behave identically from the tenant's side: nothing they can press helps,
 * so the screen escalates FOR them on arrival and then tells them the truth about what happened.
 * Shared as one component rather than two similar pages so the layout, the notify contract and
 * the honesty rule below cannot drift apart; each screen supplies only its `topic` and its words.
 *
 * ESCALATION IS AUTOMATIC, not a button. The tenant is stuck on something of ours, so there is no
 * case where the right answer is "don't tell anyone" — and a button under-reports badly: if ops
 * only hear when someone taps, stuck tenants go uncounted, which is exactly how this class of
 * failure stayed invisible (847 Housr leads stalled around here; only 106 ever produced a logged
 * signal).
 *
 * Flooding is handled SERVER-SIDE by a unique dedupeKey (one alert per tenant per topic per day),
 * never by localStorage — that leaks on a second device, a cleared cache or incognito, which is
 * precisely when a frustrated tenant reloads.
 *
 * And we only claim ops know once cpcore confirms DELIVERY. Telling a stuck tenant we have it
 * when nothing reached the channel is worse than telling them to message us.
 *
 * @param {object}  theme        partner theme (background, text and heading colours)
 * @param {string}  topic        allow-listed cpcore support topic — drives the Slack wording and
 *                               the dedupe key. See cpcore `support-topics.ts`.
 * @param {string}  reason       one line for the ops audit ledger
 * @param {string}  problemLine  the tenant's own words for what is wrong, used in WhatsApp
 * @param {object}  copy         `{ idle, sent, failed }`, each `{ title, body }`
 * @param {string}  partnerName  optional partner override; otherwise read from localStorage
 * @param {node}    action       optional self-serve action (e.g. "Try again"). Rendered
 *                               IMMEDIATELY, not gated on the escalation: where the tenant has a
 *                               real fix of their own, they should never wait on us to finish
 *                               telling ops about them.
 */
export default function SupportEscalationScreen({
  theme,
  topic,
  reason,
  problemLine,
  copy,
  partnerName,
  action,
}) {
  const partner = partnerName || localStorage.getItem("partnerIdentifier");
  const userIdentifier = localStorage.getItem("user_identifier");

  // idle -> sent | error. Starts idle because the notify fires on mount.
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    let cancelled = false;

    const notifyOps = async () => {
      try {
        const response = await fetch(
          `${config.API_URL}/b2b/public/support-request`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              topic,
              reason,
              partner: partner || undefined,
              userIdentifier: userIdentifier || undefined,
            }),
          },
        );
        const result = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok || result?.delivered !== true) {
          throw new Error(result?.reason || `HTTP ${response.status}`);
        }
        setStatus("sent");
      } catch (error) {
        console.error("Could not notify ops about this tenant", error);
        if (!cancelled) setStatus("error");
      }
    };

    notifyOps();
    return () => {
      cancelled = true;
    };
  }, [topic, reason, partner, userIdentifier]);

  const sent = status === "sent";
  const failed = status === "error";
  const words = sent ? copy.sent : failed ? copy.failed : copy.idle;

  // NOTE: no reference/id block on these screens, deliberately. Showing a tenant an internal
  // handle (lead id, magic-link id) leaks how we file them and reads as bureaucracy on a page
  // whose job is reassurance. Identity travels on the WhatsApp hand-off instead, which carries
  // their name, registered phone and lead id straight to the people who need it. What a
  // SCREENSHOT has to convey is the reason, and each screen's copy says it plainly.

  return (
    <JourneyMessageScreen
      theme={theme}
      // A quiet brand-tinted badge rather than a full-bleed gradient panel: it carries the status
      // without shouting, which keeps the page reading as calm guidance instead of an error.
      icon={
        sent ? (
          <Check className={`h-6 w-6 ${theme?.headingColor}`} strokeWidth={2} />
        ) : (
          <ShieldCheck
            className={`h-6 w-6 ${theme?.headingColor}`}
            strokeWidth={1.75}
          />
        )
      }
      title={words.title}
      body={words.body}
    >
      {action && <div className="mt-6 w-full">{action}</div>}

      {/* Contact options appear once we know the outcome. Showing them while the notify call is
          still in flight would invite a duplicate request for something already handled. */}
      {(sent || failed) && (
        <SupportContactActions
          theme={theme}
          problemLine={problemLine}
          partnerName={partner}
        />
      )}
    </JourneyMessageScreen>
  );
}
